import { SupplyFillStrategyPicker } from '@/modules/supply/components/SupplyFillStrategyPicker';
import { SupplyFillToTargetPanel } from '@/modules/supply/components/SupplyFillToTargetPanel';
import { FILL_STRATEGY_OPTIONS, type LoadRestriction } from '@/modules/supply/hooks/useLoadRestriction';
import type { SupplyAgentFillObjective, SupplyAgentShipmentMode } from '@/services/supplyAgentService';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const FIELD = 'inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700';
const NUMBER_INPUT = 'rounded border border-slate-200 px-1.5 py-0.5 text-right text-xs tabular-nums text-slate-900 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-200';

/** Opciones de «Aplicar restricción logística» y propuesta del motor, a la vista cuando se despliega el botón. */
export function LoadRestrictionPanel({ restriction, lockedCount, t }: { restriction: LoadRestriction; lockedCount: number; t: Translate }) {
  const {
    open, objective, setObjective, targetAmount, setTargetAmount, saving, setSaving, targetPallets, setTargetPallets,
    strategy, setStrategy, expandAssortment, setExpandAssortment, shipmentMode, setShipmentMode, contractedVolume,
    setContractedVolume, contractedWeight, setContractedWeight, lclWithoutContract, proposal, setProposal, accepted, setAccepted,
    loading, error, request, apply,
  } = restriction;
  if (!open && !proposal && !error) return null;
  return (
    <div className="space-y-3 pt-3">
      {open ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold text-slate-800">{t('supply.supplyAgent.loadRestrictionTitle')}</p>
          <p className="mt-0.5 text-[11px] text-slate-600">{t('supply.supplyAgent.loadRestrictionHint')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <label className={FIELD}>
              <span>{t('supply.supplyAgent.shipmentLabel')}</span>
              <select
                value={shipmentMode}
                onChange={(event) => { setShipmentMode(event.currentTarget.value as SupplyAgentShipmentMode); setProposal(null); }}
                className="rounded border-0 bg-transparent py-0.5 pl-0 pr-6 text-xs font-semibold text-slate-900 focus:ring-0"
              >
                <option value="fcl">{t('supply.supplyAgent.shipmentFcl')}</option>
                <option value="lcl">{t('supply.supplyAgent.shipmentLcl')}</option>
              </select>
            </label>
            {shipmentMode === 'lcl' ? (
              <>
                <label className={FIELD}>
                  <span>{t('supply.supplyAgent.shipmentContractedVolume')}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    value={contractedVolume}
                    onChange={(event) => { setContractedVolume(event.currentTarget.value); setProposal(null); }}
                    className={`w-20 ${NUMBER_INPUT}`}
                  />
                </label>
                <label className={FIELD}>
                  <span>{t('supply.supplyAgent.shipmentContractedWeight')}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    value={contractedWeight}
                    onChange={(event) => { setContractedWeight(event.currentTarget.value); setProposal(null); }}
                    className={`w-24 ${NUMBER_INPUT}`}
                  />
                </label>
              </>
            ) : null}
            <label className={FIELD}>
              <span>{t('supply.supplyAgent.fillToTargetObjectiveLabel')}</span>
              <select
                value={objective}
                onChange={(event) => { setObjective(event.currentTarget.value as SupplyAgentFillObjective); setProposal(null); }}
                className="rounded border-0 bg-transparent py-0.5 pl-0 pr-6 text-xs font-semibold text-slate-900 focus:ring-0"
              >
                <option value="container">{t('supply.supplyAgent.fillToTargetObjectiveContainer')}</option>
                <option value="amount">{t('supply.supplyAgent.fillToTargetObjectiveAmount')}</option>
                <option value="pallet">{t('supply.supplyAgent.fillToTargetObjectivePallet')}</option>
                <option value="layer">{t('supply.supplyAgent.fillToTargetObjectiveLayer')}</option>
              </select>
            </label>
            {objective === 'amount' ? (
              <>
                <label className={FIELD}>
                  <span>{t('supply.supplyAgent.fillToTargetAmountLabel')}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    value={targetAmount}
                    placeholder={t('supply.supplyAgent.fillToTargetAmountPlaceholder')}
                    onChange={(event) => { setTargetAmount(event.currentTarget.value); setProposal(null); }}
                    className={`w-24 ${NUMBER_INPUT}`}
                  />
                </label>
                <label title={t('supply.supplyAgent.fillToTargetSavingHelp')} className={FIELD}>
                  <span>{t('supply.supplyAgent.fillToTargetSavingLabel')}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    value={saving}
                    placeholder={t('supply.supplyAgent.fillToTargetSavingPlaceholder')}
                    onChange={(event) => { setSaving(event.currentTarget.value); setProposal(null); }}
                    className={`w-28 ${NUMBER_INPUT}`}
                  />
                </label>
              </>
            ) : null}
            {objective === 'pallet' ? (
              <label className={FIELD}>
                <span>{t('supply.supplyAgent.fillToTargetPalletsLabel')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={targetPallets}
                  placeholder={t('supply.supplyAgent.fillToTargetPalletsPlaceholder')}
                  onChange={(event) => { setTargetPallets(event.currentTarget.value); setProposal(null); }}
                  className={`w-24 ${NUMBER_INPUT}`}
                />
              </label>
            ) : null}
            <SupplyFillStrategyPicker
              value={strategy}
              options={FILL_STRATEGY_OPTIONS}
              onChange={(code) => { setStrategy(code); setProposal(null); }}
              t={t}
            />
            <label
              title={t('supply.supplyAgent.fillToTargetExpandAssortmentHelp')}
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-2 text-xs font-medium transition-colors ${expandAssortment ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              <input
                type="checkbox"
                checked={expandAssortment}
                onChange={(event) => { setExpandAssortment(event.currentTarget.checked); setProposal(null); }}
                className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              {t('supply.supplyAgent.fillToTargetExpandAssortment')}
            </label>
            <button
              type="button"
              onClick={() => void request(undefined)}
              disabled={loading}
              className="rounded-md border border-slate-400 bg-white px-3 py-2 text-xs font-semibold text-slate-900 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? t('supply.supplyAgent.fillToTargetLoading') : t('supply.supplyAgent.loadRestrictionSimulate')}
            </button>
          </div>
          {lclWithoutContract ? <p className="mt-2 text-[11px] font-medium text-amber-800">{t('supply.supplyAgent.shipmentLclNeedsContract')}</p> : null}
        </div>
      ) : null}
      {error ? <p role="alert" className="text-xs text-red-700">{error}</p> : null}
      {proposal ? (
        <SupplyFillToTargetPanel
          proposal={proposal}
          accepted={accepted}
          onToggle={(productId, isAccepted) => setAccepted((current) => ({ ...current, [productId]: isAccepted }))}
          onApply={() => void apply()}
          onDiscard={() => setProposal(null)}
          onSelectOption={(option) => void request(option)}
          switchingOption={loading}
          lockedCount={lockedCount}
          t={t}
        />
      ) : null}
    </div>
  );
}
